import { Tag, useModal } from '@omnipresentgroup/design-system';
import { useXeroConnection } from '@omnipresentgroup/domains';
import { XeroConnectionStatusEnum } from '@omnipresentgroup/types';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import XeroLogo from './../../../assets/xero-logo.svg';
import {
  IntegrationWidget,
  type IntegrationWidgetStatus,
} from '../../components';
import { XERO_CONFIGURATION_PATH } from '../../constants';
import { useManagerUser } from '../../hooks';
import { WrappedMixpanel } from '../../mixpanelWrapper';
import { XeroActionsRequiredModal, XeroIntegrationModal } from './components';
import { XeroDisconnectModal } from './XeroDisconnectModal';

const INTEGRATION_TITLE = 'Xero';
const ENABLE_INTEGRATION_TEXT =
  'Connect your Xero account for a seamless accounting integration that receives itemised Omnipresent invoices.';
const DISCONNECT_LABEL = 'Disconnect from Xero';
const CONFIGURE_LABEL = 'Configure';
const WIDGET_TAG_LABEL = 'Accounting';
const XERO_INTEGRATION_CONNECT_BUTTON_LABEL = 'Connect now';
const ACTION_REQUIRED = 'Action required';

export const XeroIntegrationWidget = () => {
  const { modalProps } = useModal({});

  const [moreInfoModalOpen, setMoreInfoModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [actionRequiredModalOpen, setActionRequiredModalOpen] = useState(false);

  const navigate = useNavigate();

  const { userId, companyId } = useManagerUser();

  const { data: connectionStatus, isLoading: isConnectionStatusLoading } =
    useXeroConnection({
      clientId: companyId,
      enabled: !!companyId,
      throwOnError: false,
    });

  const canConfigure = connectionStatus?.canUserConfigureConnection || false;
  const isConnected =
    connectionStatus?.connectionStatus === XeroConnectionStatusEnum.CONNECTED;
  const isNotConnected =
    connectionStatus?.connectionStatus ===
    XeroConnectionStatusEnum.NOT_CONNECTED;
  const actionsNeeded =
    connectionStatus?.requiredActions &&
    connectionStatus?.requiredActions.length > 0;

  const mixPanelProperties = useMemo(
    () => ({
      companyId,
      userId,
    }),
    [companyId, userId],
  );

  const xeroStatus: IntegrationWidgetStatus | JSX.Element = useMemo(() => {
    if (actionsNeeded) {
      return (
        <Tag
          id={'connection-status-tag-xero'}
          intent="warning"
          onClick={() => setActionRequiredModalOpen(true)}
        >
          {ACTION_REQUIRED}
        </Tag>
      );
    }

    return isConnected ? 'connected' : 'not_connected';
  }, [actionsNeeded, isConnected]);

  const handleLearnMoreClick = () => {
    WrappedMixpanel.track(
      'Xero Integration Read more clicked',
      mixPanelProperties,
    );
    setMoreInfoModalOpen(true);
  };

  return (
    <>
      <IntegrationWidget
        id="xero"
        isLoading={isConnectionStatusLoading}
        key="xero-integration-widget"
      >
        <IntegrationWidget.Header
          title={INTEGRATION_TITLE}
          logo={XeroLogo}
          id="xero"
          status={xeroStatus}
          isBeta={true}
          options={
            isConnected && canConfigure
              ? [
                  {
                    name: DISCONNECT_LABEL,
                    onClick: () => setDisconnectModalOpen(true),
                  },
                  {
                    name: CONFIGURE_LABEL,
                    onClick: () =>
                      navigate(XERO_CONFIGURATION_PATH, {
                        state: {
                          from: 'XeroIntegrationWidget',
                        },
                      }),
                  },
                ]
              : undefined
          }
        />
        <IntegrationWidget.Body
          content={ENABLE_INTEGRATION_TEXT}
          tags={[WIDGET_TAG_LABEL]}
        />
        <IntegrationWidget.Footer
          learnMoreHandle={handleLearnMoreClick}
          {...(!isConnected && {
            actionButtonLabel: XERO_INTEGRATION_CONNECT_BUTTON_LABEL,
          })}
        />
      </IntegrationWidget>
      <XeroIntegrationModal
        modalProps={{
          ...modalProps,
          open: moreInfoModalOpen,
          onOpenChange: setMoreInfoModalOpen,
        }}
        handleClose={() => setMoreInfoModalOpen(false)}
        canConfigure={canConfigure}
        isNotConnected={isNotConnected}
      />
      {disconnectModalOpen && (
        <XeroDisconnectModal
          modalProps={{
            ...modalProps,
            open: disconnectModalOpen,
            onOpenChange: setDisconnectModalOpen,
          }}
          handleClose={() => setDisconnectModalOpen(false)}
          clientId={companyId}
        />
      )}
      {actionRequiredModalOpen && (
        <XeroActionsRequiredModal
          actions={connectionStatus?.requiredActions}
          modalProps={{
            ...modalProps,
            open: actionRequiredModalOpen,
            onOpenChange: setActionRequiredModalOpen,
          }}
          handleClose={() => setActionRequiredModalOpen(false)}
        />
      )}
    </>
  );
};
